import { fetchWithTimeout, API_URL } from './api.js';

/**
 * Button Group Registry
 * Caches button group definitions fetched from backend
 * Provides synchronous access to button groups by ID
 */
export class ButtonGroupRegistry {
  constructor(apiBaseURL = null) {
    this.groups = new Map();
    this.loaded = false;
    this.apiBaseURL = apiBaseURL || API_URL;
  }

  /**
   * Load all button groups from backend
   * Call once on app initialization
   * @returns {Promise<boolean>} True if loaded successfully
   */
  async load() {
    try {
      const response = await fetchWithTimeout(`${this.apiBaseURL}/config/button-groups`, {
        credentials: 'include'
      });

      if (!response.ok) {
        console.error('[ButtonGroupRegistry] Failed to fetch button groups:', response.status);
        return false;
      }

      const result = await response.json();

      if (result.success && result.data?.groups) {
        // Store all groups in Map
        Object.entries(result.data.groups).forEach(([id, group]) => {
          this.groups.set(id, group);
        });

        this.loaded = true;
        console.log(`[ButtonGroupRegistry] Loaded ${this.groups.size} button groups`);
        return true;
      }

      return false;

    } catch (error) {
      console.error('[ButtonGroupRegistry] Error loading button groups:', error);
      return false;
    }
  }

  /**
   * Get a button group by ID (synchronous)
   * @param {string} groupId - Button group identifier
   * @returns {Object|null} Button group definition or null if not found
   */
  get(groupId) {
    if (!this.loaded) {
      console.warn('[ButtonGroupRegistry] Registry not loaded yet');
      return null;
    }

    const group = this.groups.get(groupId);

    if (!group) {
      console.warn(`[ButtonGroupRegistry] Button group not found: ${groupId}`);
      return null;
    }

    return group;
  }

  /**
   * Check if a button group exists
   * @param {string} groupId - Button group identifier
   * @returns {boolean} True if group exists
   */
  has(groupId) {
    return this.groups.has(groupId);
  }

  /**
   * Get all button group IDs
   * @returns {Array<string>} Array of group IDs
   */
  getGroupIds() {
    return Array.from(this.groups.keys());
  }

  /**
   * Check if registry is loaded
   * @returns {boolean} True if loaded
   */
  isLoaded() {
    return this.loaded;
  }
}
